import { useEffect, useRef } from "react";
import type { Route } from "./+types/about";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { BrilliantCut } from "~/components/marketing/svg/BrilliantCut";
import { GoldMesh } from "~/components/marketing/svg/GoldMesh";
import { RingSketch } from "~/components/marketing/svg/RingSketch";
import { CastingDiagram } from "~/components/marketing/svg/CastingDiagram";
import { ProcessFlow } from "~/components/marketing/svg/ProcessFlow";
import { JewelryIcon } from "~/components/marketing/svg/JewelryIcons";
import { detectLocale, translations } from "~/i18n/index";
import { useT } from "~/i18n/context";
import { socialMeta } from "~/lib/seo";

export async function loader({ request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  return { locale };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = data?.locale ?? "en";
  const t = translations[locale].about.meta;
  return [
    { title: t.title },
    { name: "description", content: t.description },
    ...socialMeta({ title: t.title, description: t.description, pathname: "/about", locale }),
  ];
}

function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add("visible"); },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

function Rule({ className = "" }: { className?: string }) {
  return <div className={`rule ${className}`} />;
}

export default function AboutPage() {
  const { t } = useT();
  const h = t.about;

  const statsRef = useReveal(0.08);
  const originRef = useReveal();
  const timelineRef = useReveal(0.06);
  const visionRef = useReveal();
  const leadershipRef = useReveal();
  const quoteRef = useReveal();
  const ctaRef = useReveal();

  return (
    <MarketingLayout>

      {/* ══ HERO ══ */}
      <section className="px-6 lg:px-12 pt-16 pb-16 lg:pt-24 lg:pb-20 relative overflow-hidden">
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          {Array.from({ length: 12 }).map((_, i) => (<line key={`v${i}`} x1={`${(i + 1) * 8}%`} y1="0" x2={`${(i + 1) * 8}%`} y2="100%" stroke="oklch(0.085 0.004 50)" strokeWidth="1" opacity="0.022" />))}
          {Array.from({ length: 5 }).map((_, i) => (<line key={`h${i}`} x1="0" y1={`${(i + 1) * 18}%`} x2="100%" y2={`${(i + 1) * 18}%`} stroke="oklch(0.085 0.004 50)" strokeWidth="1" opacity="0.014" />))}
        </svg>
        <div className="absolute top-0 right-0 translate-x-1/4 -translate-y-1/4 pointer-events-none" aria-hidden="true">
          <BrilliantCut size={560} opacity={0.055} animated />
        </div>

        <p className="issue-label mb-7 relative" style={{ fontFamily: "var(--font-sans)" }}>{h.hero.overline}</p>

        <h1 className="font-display font-light leading-[0.92] tracking-tight text-foreground max-w-5xl relative" style={{ fontSize: "clamp(3.5rem, 8.5vw, 8rem)" }}>
          {h.hero.h1Line1}
          <br />
          {h.hero.h1Line2}
          <br />
          <em className="not-italic" style={{ fontWeight: 300, opacity: 0.4 }}>{h.hero.h1Line3}</em>
        </h1>

        <div className="mt-12 max-w-2xl relative">
          <p className="font-display font-light text-muted-foreground" style={{ fontSize: "clamp(1.05rem, 1.8vw, 1.35rem)", lineHeight: 1.55 }}>{h.hero.tagline}</p>
        </div>

        <p className="mt-10 issue-label relative" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.hero.lastUpdated}</p>
      </section>

      {/* ══ STATS ══ */}
      <div className="px-6 lg:px-12">
        <Rule />
        <div ref={statsRef} className="reveal">
          <div className="flex items-baseline justify-between py-4 mb-8">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.stats.overline}</span>
            <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.stats.subtitle}</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-0">
            {h.stats.items.map((s, i) => (
              <div key={s.label} className="relative">
                {i > 0 ? <div className="hidden lg:block absolute inset-y-0 left-0 w-px bg-border" /> : null}
                <div className="pb-10" style={{ paddingLeft: i > 0 ? "var(--col-pad-sm)" : undefined, paddingRight: i < h.stats.items.length - 1 ? "var(--col-pad-sm)" : undefined, transitionDelay: `${i * 80}ms` }}>
                  <Rule className="mb-5" />
                  <p className="font-display font-light leading-none mb-2" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", color: "var(--teal-sapphire)" }}>{s.value}</p>
                  <p className="issue-label leading-snug" style={{ fontFamily: "var(--font-sans)", opacity: 0.5, maxWidth: "16ch" }}>{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <Rule />
      </div>

      {/* ══ ORIGIN ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-24">
        <div ref={originRef} className="reveal">
          <div className="flex items-baseline justify-between mb-10">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.origin.overline}</span>
            <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.origin.subtitle}</span>
          </div>
          <div className="grid lg:grid-cols-[1fr_1fr] gap-0">
            <div className="py-8 pr-0 lg:pr-16 space-y-6 border-b lg:border-b-0 lg:border-r border-border">
              <Rule />
              <p className="font-display font-light text-foreground" style={{ fontSize: "clamp(1.35rem, 2.2vw, 1.8rem)", lineHeight: 1.45, maxWidth: "36ch" }}>{h.origin.p1}</p>
              <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.86rem", maxWidth: "44ch" }}>{h.origin.p2}</p>
            </div>
            <div className="hidden lg:grid grid-cols-2 gap-0 pl-10">
              <div className="relative overflow-hidden border-r border-border" style={{ aspectRatio: "3/4" }}>
                <img src="https://images.unsplash.com/photo-1631982690223-8aa4be0a2497?auto=format&fit=crop&w=400&h=533&q=80" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "saturate(0.45) contrast(1.08)" }} loading="lazy" />
                <div className="absolute inset-0" style={{ background: "oklch(0.97 0.006 90 / 0.52)" }} />
                <div className="absolute inset-0 flex items-center justify-center p-8"><RingSketch className="w-full max-w-[180px] opacity-80" animated /></div>
                <div className="absolute bottom-4 left-4"><span className="issue-label" style={{ fontFamily: "var(--font-sans)", opacity: 0.55 }}>{h.origin.digitalDesign}</span></div>
              </div>
              <div className="relative overflow-hidden" style={{ aspectRatio: "3/4" }}>
                <img src="https://images.unsplash.com/photo-1603561591411-07134e71a2a9?auto=format&fit=crop&w=400&h=533&q=80" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "saturate(0.4) contrast(1.1)" }} loading="lazy" />
                <div className="absolute inset-0" style={{ background: "oklch(0.93 0.006 60 / 0.55)" }} />
                <div className="absolute inset-0 flex items-center justify-center p-6"><CastingDiagram className="w-full max-w-[200px] opacity-70" animated /></div>
                <div className="absolute bottom-4 left-4"><span className="issue-label" style={{ fontFamily: "var(--font-sans)", opacity: 0.55 }}>{h.origin.precisionCraft}</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><Rule /></div>

      {/* ══ TIMELINE ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-24">
        <div ref={timelineRef} className="reveal">
          <div className="flex items-baseline justify-between mb-14">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.timeline.overline}</span>
            <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.timeline.subtitle}</span>
          </div>

          {/* Desktop alternating */}
          <div className="hidden lg:block relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border" style={{ transform: "translateX(-0.5px)" }} aria-hidden="true" />
            <div className="space-y-0">
              {h.timeline.phases.map((phase, i) => {
                const isLeft = i % 2 === 0;
                return (
                  <div key={phase.label} className="relative grid grid-cols-2 gap-0" style={{ transitionDelay: `${i * 100}ms` }}>
                    <div className="absolute left-1/2 top-12 w-2.5 h-2.5 rounded-full" style={{ transform: "translate(-50%, -50%)", background: "var(--teal-sapphire)", opacity: 0.7 }} aria-hidden="true" />
                    {isLeft ? (
                      <>
                        <div className="pr-16 pb-20 pt-4">
                          <Rule className="mb-6" />
                          <div className="flex items-baseline gap-4 mb-4">
                            <span className="font-display font-light select-none" style={{ fontSize: "clamp(2.5rem, 4vw, 3.8rem)", lineHeight: 1, color: "var(--primary)", opacity: 0.45 }}>{String(i + 1).padStart(2, "0")}</span>
                            <div>
                              <span className="issue-label block mb-1" style={{ fontFamily: "var(--font-sans)", opacity: 0.4 }}>{phase.label}</span>
                              <h3 className="font-display font-normal text-foreground leading-tight" style={{ fontSize: "clamp(1.3rem, 2vw, 1.7rem)" }}>{phase.heading}</h3>
                            </div>
                          </div>
                          <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.83rem", maxWidth: "42ch" }}>{phase.body}</p>
                        </div>
                        <div />
                      </>
                    ) : (
                      <>
                        <div />
                        <div className="pl-16 pb-20 pt-4">
                          <Rule className="mb-6" />
                          <div className="flex items-baseline gap-4 mb-4">
                            <span className="font-display font-light select-none" style={{ fontSize: "clamp(2.5rem, 4vw, 3.8rem)", lineHeight: 1, color: "var(--primary)", opacity: 0.45 }}>{String(i + 1).padStart(2, "0")}</span>
                            <div>
                              <span className="issue-label block mb-1" style={{ fontFamily: "var(--font-sans)", opacity: 0.4 }}>{phase.label}</span>
                              <h3 className="font-display font-normal text-foreground leading-tight" style={{ fontSize: "clamp(1.3rem, 2vw, 1.7rem)" }}>{phase.heading}</h3>
                            </div>
                          </div>
                          <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.83rem", maxWidth: "42ch" }}>{phase.body}</p>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile stacked */}
          <div className="lg:hidden space-y-0">
            {h.timeline.phases.map((phase, i) => (
              <div key={phase.label}>
                <Rule />
                <div className="py-8">
                  <div className="flex items-baseline gap-4 mb-4">
                    <span className="font-display font-light select-none" style={{ fontSize: "clamp(1.8rem, 5vw, 3rem)", lineHeight: 1, color: "var(--primary)", opacity: 0.45 }}>{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <span className="issue-label block mb-1" style={{ fontFamily: "var(--font-sans)", opacity: 0.4 }}>{phase.label}</span>
                      <h3 className="font-display font-normal text-foreground leading-tight text-xl">{phase.heading}</h3>
                    </div>
                  </div>
                  <p className="text-muted-foreground leading-relaxed text-sm">{phase.body}</p>
                </div>
              </div>
            ))}
            <Rule />
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><Rule /></div>

      {/* ══ VISION & MISSION ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-20">
        <div ref={visionRef} className="reveal">
          <div className="flex items-baseline justify-between mb-10">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.vision.overline}</span>
          </div>
          <div className="grid lg:grid-cols-2 gap-0">
            <div className="py-8 pr-0 lg:pr-16 border-b lg:border-b-0 lg:border-r border-border">
              <Rule className="mb-6" />
              <JewelryIcon type="viewer-3d" size={36} className="mb-5 opacity-40" />
              <p className="issue-label mb-4" style={{ fontFamily: "var(--font-sans)" }}>{h.vision.visionLabel}</p>
              <p className="font-display font-light text-foreground" style={{ fontSize: "clamp(1.1rem, 1.8vw, 1.45rem)", lineHeight: 1.5, maxWidth: "38ch" }}>{h.vision.visionBody}</p>
            </div>
            <div className="py-8 pl-0 lg:pl-16">
              <Rule className="mb-6" />
              <JewelryIcon type="production" size={36} className="mb-5 opacity-40" />
              <p className="issue-label mb-4" style={{ fontFamily: "var(--font-sans)" }}>{h.vision.missionLabel}</p>
              <p className="font-display font-light text-foreground" style={{ fontSize: "clamp(1.1rem, 1.8vw, 1.45rem)", lineHeight: 1.5, maxWidth: "38ch" }}>{h.vision.missionBody}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><Rule /></div>

      {/* ══ HOW IT WORKS ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-20">
        <div className="flex items-baseline justify-between mb-10">
          <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.howItWorks.overline}</span>
          <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.howItWorks.subtitle}</span>
        </div>
        <ProcessFlow steps={4} className="mb-6 hidden lg:block" />
        <div className="grid lg:grid-cols-4 gap-0">
          {h.howItWorks.steps.map((step, i) => (
            <div key={i} className="relative">
              {i > 0 ? <div className="hidden lg:block absolute inset-y-0 left-0 w-px bg-border" /> : null}
              <div className="py-8" style={{ paddingLeft: i > 0 ? "var(--col-pad)" : undefined, paddingRight: i < h.howItWorks.steps.length - 1 ? "var(--col-pad)" : undefined }}>
                <Rule className="mb-5" />
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="font-display font-light text-foreground/[0.14]" style={{ fontSize: "clamp(2rem, 3vw, 2.6rem)", lineHeight: 1 }}>{["I","II","III","IV"][i]}</span>
                  <h3 className="font-display font-normal text-foreground" style={{ fontSize: "clamp(1rem, 1.4vw, 1.2rem)" }}>{step.heading}</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.8rem", maxWidth: "28ch" }}>{step.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="px-6 lg:px-12"><Rule /></div>

      {/* ══ LEADERSHIP ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-20">
        <div ref={leadershipRef} className="reveal">
          <div className="flex items-baseline justify-between mb-10">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.leadership.overline}</span>
          </div>
          <div className="grid lg:grid-cols-[280px_1fr] gap-0 items-start">
            <div className="border-r border-border pr-10 hidden lg:block">
              <div className="overflow-hidden bg-muted" style={{ aspectRatio: "3/4", maxWidth: "200px" }}>
                <img src="https://images.unsplash.com/photo-1633810542706-90e5ff7557be?auto=format&fit=crop&w=400&h=533&q=80" alt="Founder" className="w-full h-full object-cover object-top" style={{ filter: "grayscale(0.12) contrast(1.04)" }} loading="lazy" />
              </div>
            </div>
            <div className="pl-0 lg:pl-10 pt-4 lg:pt-0">
              <Rule className="mb-6" />
              <p className="issue-label mb-3" style={{ fontFamily: "var(--font-sans)" }}>{h.leadership.founderRole}</p>
              <h2 className="font-display font-light text-foreground mb-6 leading-tight" style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)" }}>{h.leadership.founderName}</h2>
              <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.86rem", maxWidth: "52ch" }}>{h.leadership.founderBio}</p>
              <div className="mt-8 flex flex-wrap gap-0">
                {h.leadership.tags.map((tag, i) => (
                  <div key={tag} className={`py-2 ${i > 0 ? "pl-5 border-l border-border ml-5" : ""}`}>
                    <p className="issue-label" style={{ fontFamily: "var(--font-sans)", opacity: 0.5 }}>{tag}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ SUPPLY CHAIN ARCHITECTURE ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-20 relative overflow-hidden">
        <GoldMesh opacity={0.08} />
        <div className="relative">
          <div className="px-6 lg:px-12"><Rule className="mb-10" /></div>
          <div className="grid lg:grid-cols-[1fr_520px] gap-10 items-end">
            <div>
              <span className="issue-label block mb-6" style={{ fontFamily: "var(--font-sans)" }}>{h.supplyChain.overline}</span>
              <h2 className="font-display font-light leading-[0.92] tracking-tight text-foreground" style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
                {h.supplyChain.heading.split("\n").map((line, i) => (
                  <span key={i}>{i === 0 ? line : <em className="not-italic" style={{ fontWeight: 300, opacity: 0.42 }}>{line}</em>}{i === 0 && <br />}</span>
                ))}
              </h2>
            </div>
            <div className="space-y-0">
              {h.supplyChain.items.map(({ title, desc }, i) => (
                <div key={title}>
                  {i === 0 ? <Rule /> : null}
                  <div className="py-5 grid sm:grid-cols-[180px_1fr] gap-2 sm:gap-6 items-baseline">
                    <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{title}</span>
                    <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.81rem" }}>{desc}</p>
                  </div>
                  <Rule />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><Rule /></div>

      {/* ══ PULL QUOTE ══ */}
      <section className="px-6 lg:px-12 py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
          <BrilliantCut size={600} opacity={0.05} animated />
        </div>
        <div ref={quoteRef} className="reveal max-w-4xl mx-auto text-center relative">
          <p className="font-display font-light italic text-foreground" style={{ fontSize: "clamp(1.5rem, 3.2vw, 2.6rem)", lineHeight: 1.38 }}>
            {h.quote.text.split("\n").map((line, i) => (
              <span key={i}>{line}{i === 0 && <><br className="hidden lg:inline" />{" "}</>}</span>
            ))}
          </p>
          <div className="mt-10 flex items-center justify-center gap-6">
            <div className="w-16 rule" />
            <span className="issue-label shrink-0" style={{ fontFamily: "var(--font-sans)" }}>{h.quote.attribution}</span>
            <div className="w-16 rule" />
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><Rule /></div>

      {/* ══ FOOTER CTA ══ */}
      <section className="px-6 lg:px-12 py-20 lg:py-28">
        <div ref={ctaRef} className="reveal grid lg:grid-cols-[1fr_auto] items-end gap-10">
          <div>
            <span className="issue-label block mb-5" style={{ fontFamily: "var(--font-sans)" }}>{h.cta.overline}</span>
            <h2 className="font-display font-light leading-[0.92] tracking-tight text-foreground" style={{ fontSize: "clamp(3rem, 7vw, 6.5rem)" }}>
              {h.cta.heading.split("\n").map((line, i) => (
                <span key={i}>{i === 0 ? line : <em className="not-italic" style={{ fontWeight: 300, opacity: 0.42 }}>{line}</em>}{i === 0 && <br />}</span>
              ))}
            </h2>
          </div>
          <div className="pb-2 space-y-4">
            <a href="/sign-in?redirect=/trade/catalog" className="block px-8 py-4 bg-teal text-teal-fg hover:bg-teal-hover transition-colors text-center" style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>{h.cta.primary}</a>
            <a href="/brands" className="block text-center issue-label hover:text-foreground transition-colors" style={{ fontFamily: "var(--font-sans)", opacity: 0.45 }}>{h.cta.secondary1}</a>
            <a href="/designers" className="block text-center issue-label hover:text-foreground transition-colors" style={{ fontFamily: "var(--font-sans)", opacity: 0.45 }}>{h.cta.secondary2}</a>
          </div>
        </div>
      </section>

    </MarketingLayout>
  );
}
